// src/hooks/useSubscription.ts
import { useState, useEffect, useCallback } from 'react';
import {
  Subscription,
  getSubscriptionStatus
} from '../utils/subscriptionApi';
import { fetchBillingSubscriptionApi } from '../services/billingService';
import { isAdmin } from '../utils/auth';

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  hasActiveSubscription: boolean;
  isTrialing: boolean;
  isPastDue: boolean;
  daysUntilRenewal: number | null;
  daysRemainingInTrial: number | null;
}

export function useSubscription(): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Check if user is an administrator
      if (isAdmin()) {
        // Call the billing-specific endpoint for administrators
        const billingData = await fetchBillingSubscriptionApi();

        // Transform BillingSubscription to Subscription format
        const transformedData: Subscription = {
          id: billingData.id,
          status: billingData.status === 'inactive' ? 'canceled' : billingData.status,
          planTier: billingData.planTier || '',
          planName: billingData.planName || '',
          planId: billingData.planId,
          currentTierLevel: billingData.currentTierLevel,
          currentMaxAthletes: billingData.currentMaxAthletes,
          maxAthletes: billingData.maxAthletes,
          currentPeriodStart: billingData.currentPeriodStart || '',
          currentPeriodEnd: billingData.currentPeriodEnd || '',
          cancelAtPeriodEnd: billingData.cancelAtPeriodEnd || false,
          trialEnd: billingData.trialEnd || null,
          amount: billingData.amount || 0,
          currency: billingData.currency || 'usd',
          interval: billingData.interval || 'month',
          intervalCount: billingData.intervalCount ?? 1,
          paymentMethodSummary: billingData.paymentMethodSummary,
          paymentMethod: billingData.paymentMethod || null,
          gracePeriodEnd: billingData.gracePeriodEnd || null,
          organizationDisplayName: billingData.organizationDisplayName || null,
          planCategory: billingData.planCategory || null,
          displayMetadata: billingData.displayMetadata || {},
        };

        setSubscription(transformedData);
      } else {
        // Use the standard subscription endpoint for non-admin users
        const data = await getSubscriptionStatus();
        setSubscription(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch subscription');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);


  // Computed properties
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isTrialing = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';

  const daysUntilRenewal = subscription?.currentPeriodEnd
    ? Math.ceil((new Date(subscription.currentPeriodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const daysRemainingInTrial = subscription?.trialEnd
    ? Math.ceil((new Date(subscription.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return {
    subscription,
    isLoading,
    error,
    refetch: fetchSubscription,
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    daysUntilRenewal,
    daysRemainingInTrial
  };
}

